import { format } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useUtilizationPractice } from '../../../onboarding/practice/utilizationPractice';
import { usePracticeDataset } from '../../../onboarding/practice/practiceDataset';
import { PracticeScopeProvider } from '../../../onboarding/practice/practiceScope';
import { PRACTICE_DISH_ID, PRACTICE_ID_PREFIX, isPracticeId } from '../../../onboarding/practice/practiceGuards';
import { publishUtilizationValues } from '../../../onboarding/utilizationValues';
import { useTranslation } from 'react-i18next';
import { useOrderHistory } from '../../../../stores/appOrderHistoryStore';
import { useRevenueHistory } from '../../../../stores/appRevenueHistoryStore';
import { useCostPriceStore } from '../../../../stores/appCostPriceStore';
import { useSubrecipesStore } from '../../../../stores/appSubrecipesStore';
import { useRestaurantStore } from '../../../../stores/appRestaurantStore';
import { useWasteStore } from '../../../../stores/appWasteStore';
import { updateRestaurantFavoriteProducts } from '../../../../services/restaurant/restaurantService';
import { useAppUserDefaultStore, isAllRestaurantsSelected } from '../../../../stores/appUserDefaultStore';
import { useTimeIntervalStore } from '../../../../stores/appTimeIntervalStore';
import { useFiltersStore } from '../../../../stores/appFiltersStore';
import { useAuth } from '../../../../services/authentication/authContext';
import { canAccessCostPriceAutomation, canAccessWastePage, canUseSuperAdminFeatures } from '../../../../services/accessManagement/accessManagement';
import { useImpersonationStore } from '../../../../stores/appImpersonationStore';
import {
  useLoadOrderHistory,
  useLoadRevenueHistory,
  useLoadCostPrice,
  useLoadSubrecipes,
  useLoadWaste,
} from '../../../../utils/dataLoadingUtils';
import { enrichCostPriceListWithCurrentPrices } from '../../../../utils/costPriceUtils';
import { getTableFormattedDate } from '../../../../utils/dateUtils';
import { resolveWastePercentage } from '../../../../utils/wasteUtils';
import { useByRestaurantCostData } from '../../../../hooks/useByRestaurantCostData';
import { rangeFromTimeControls, inRange } from '../../../../utils/reports/shared';
import { cachedReportValue } from '../../../../utils/reports/reportMemoCache';
import {
  computeWasteByProduct,
  EMPTY_WASTE_BY_PRODUCT,
  WasteByProductRow,
} from '../../../../utils/reports/wasteByProduct';
import { EmptyCard, ReportLoading } from './common';
import {
  computeUtilizationReport,
  getUtilizationStatus,
  filterRowsByTab,
  filterGroupedView,
  applyUtilizationFilters,
  applyUtilizationFiltersToGroups,
  applyUnmappedFilters,
  isOverOrdered,
  isUnderOrdered,
  isRecipeAttributable,
  isUnmappedTab,
  EMPTY_UTILIZATION_FILTERS,
  UtilizationTab,
  UtilizationFilters,
  UtilizationGroupMember,
} from '../../../../utils/reports/utilizationReport';
import ProductSetupModal from '../../costPrice/new/CostPrice/ProductSetupModal';
import CostPriceDetailModal from '../../costPrice/new/CostPrice/CostPriceDetailModal';
import UtilizationStatCards, { UnmappedStatCards } from './utilization/UtilizationStatCards';
import UtilizationControls from './utilization/UtilizationControls';
import OrderedVsNeededTable from './utilization/OrderedVsNeededTable';
import PurchasedNotLinkedTable from './utilization/PurchasedNotLinkedTable';
import WasteBreakdownCharts from './utilization/WasteBreakdownCharts';
import UsedDishesDrawer from './utilization/UsedDishesDrawer';
import OrganizationUtilizationReport from './utilization/OrganizationUtilizationReport';

/** Stable empty enrichment, so skipping the (expensive) enrichment doesn't hand the
 *  reported-waste memo a fresh array — and a new cache key — on every render. */
const EMPTY_ENRICHED: ReturnType<typeof enrichCostPriceListWithCurrentPrices> = [];

const UtilizationReportBody: React.FC<{ tourView?: boolean }> = ({ tourView = false }) => {
  const practice = useUtilizationPractice();
  const fixture = usePracticeDataset(tourView && practice.fixture);
  const suspendOverlays = practice.active && !tourView;
  const [practiceFavorites, setPracticeFavorites] = useState<string[]>([]);
  useEffect(() => { if (!fixture) setPracticeFavorites([]); }, [fixture]);
  const { t } = useTranslation();
  const { backendUser, accountSettings } = useAuth();
  const impersonationTarget = useImpersonationStore(s => s.target);
  const canCheckAlternatives = !tourView && !suspendOverlays && backendUser?.role === 'admin'
    && !!impersonationTarget && impersonationTarget.userId === backendUser.id
    && impersonationTarget.view !== 'customer' && canUseSuperAdminFeatures(backendUser)
    && canAccessCostPriceAutomation(backendUser, accountSettings);
  const canSuggestWasteActionItems = !tourView && !practice.active && backendUser?.role === 'admin'
    && !!impersonationTarget && impersonationTarget.userId === backendUser.id
    && impersonationTarget.view !== 'customer' && canUseSuperAdminFeatures(backendUser);
  // The drawer's reported-waste section only exists for accounts that log waste.
  const hasWasteAccess = canAccessWastePage(backendUser, accountSettings);

  // Data is scoped to the selected restaurant by these hooks.
  useLoadOrderHistory();
  useLoadRevenueHistory();
  useLoadCostPrice();
  useLoadSubrecipes();
  useLoadWaste();

  const liveOrders = useOrderHistory((s) => s.orderHistoryList);
  const isLoadingOrders = useOrderHistory((s) => s.isLoading);
  const liveRevenue = useRevenueHistory((s) => s.revenueHistoryList);
  const isLoadingRevenue = useRevenueHistory((s) => s.isLoading);
  const liveCostPriceList = useCostPriceStore((s) => s.costPriceList);
  const isLoadingCostPrice = useCostPriceStore((s) => s.isLoading);
  const liveCustomIngredients = useCostPriceStore((s) => s.customIngredients);
  const liveSubrecipesById = useSubrecipesStore((s) => s.subrecipesById);
  const orders = fixture?.orderHistoryList ?? liveOrders;
  const revenue = fixture?.revenueHistoryList ?? liveRevenue;
  const realCostPriceList = useMemo(() => liveCostPriceList.filter(d => !isPracticeId(d.id)), [liveCostPriceList]);
  const costPriceList = fixture?.costPriceList ?? realCostPriceList;
  const customIngredients = fixture?.customIngredients ?? liveCustomIngredients;
  const subrecipesById = fixture?.subrecipesById ?? liveSubrecipesById;
  const restaurantId = useAppUserDefaultStore((s) => s.restaurant?.id) ?? '';
  const showExVat = useAppUserDefaultStore((s) => s.showExVatOrders);
  const reportSettings = useRestaurantStore((s) => (restaurantId ? s.reportSettings[restaurantId] : undefined));
  const setReportSettings = useRestaurantStore((s) => s.setReportSettings);
  const defaultWastePercentage = useMemo(
    () => fixture ? 0 : resolveWastePercentage(reportSettings?.wastePercentage),
    [reportSettings, fixture]
  );

  // Starred products (restaurant-wide) driving the table's Favorites tab. The store
  // is the single source of truth: toggling writes it optimistically, then persists,
  // and reverts on failure.
  const favoriteKeys = useMemo(() => tourView ? practiceFavorites : reportSettings?.favoriteProducts ?? [], [reportSettings, tourView, practiceFavorites]);

  const handleToggleFavorite = useCallback(
    (key: string) => {
      if (tourView) { setPracticeFavorites(keys => keys.includes(key) ? keys.filter(k => k !== key) : [...keys, key]); return; }
      if (!restaurantId || !reportSettings) return;
      const next = favoriteKeys.includes(key) ? favoriteKeys.filter((k) => k !== key) : [...favoriteKeys, key];
      setReportSettings(restaurantId, { ...reportSettings, favoriteProducts: next });
      updateRestaurantFavoriteProducts(restaurantId, next).catch((err) => {
        console.error('Failed to save favorite products', err);
        setReportSettings(restaurantId, { ...reportSettings, favoriteProducts: favoriteKeys });
      });
    },
    [restaurantId, reportSettings, favoriteKeys, setReportSettings, tourView]
  );

  // The shared overall-type filter (all / food / drinks), driven by TopSection.
  const selectedOverallType = useFiltersStore((s) => s.overallTypeFilter);
  const overallTypeFilter = suspendOverlays ? practice.originalOverallType ?? selectedOverallType : selectedOverallType;

  // Cross-restaurant sourcing maps, exactly as the cost-price page wires them.
  const byRestaurant = useByRestaurantCostData(costPriceList, restaurantId);

  const selectedView = useTimeIntervalStore((s) => s.view);
  const selectedStartDate = useTimeIntervalStore((s) => s.startDate);
  const selectedEndDate = useTimeIntervalStore((s) => s.endDate);
  const view = suspendOverlays ? practice.originalTime?.view ?? selectedView : selectedView;
  const startDate = suspendOverlays ? practice.originalTime?.startDate ?? selectedStartDate : selectedStartDate;
  const endDate = suspendOverlays ? practice.originalTime?.endDate ?? selectedEndDate : selectedEndDate;

  const range = useMemo(() => rangeFromTimeControls(view, startDate, endDate), [view, startDate, endDate]);

  const data = cachedReportValue(
    'utilization.data',
    [orders, revenue, costPriceList, subrecipesById, defaultWastePercentage, restaurantId, view, startDate, endDate],
    () =>
      computeUtilizationReport({
        orders,
        revenue,
        costPriceList,
        subrecipesById,
        defaultWastePercentage,
        currentRestaurantId: restaurantId,
        range,
      })
  );

  // --- reported waste (drawer only) ---------------------------------------
  // Phase 2 of waste↔product linking: the entries staff logged, attributed to products by
  // the SAME walk the "needed" column uses. Display only — no number above depends on it.
  const wasteList = useWasteStore((s) => s.wasteList);
  const wasteRestaurantId = useWasteStore((s) => s.currentRestaurantId);

  /** Cheap gate, so an account with nothing logged this period allocates nothing.
   *  Also guards on the store's own restaurant: the list is replaced asynchronously on a
   *  restaurant switch, and attributing another restaurant's waste is worse than none. */
  const hasPeriodWaste =
    !fixture && hasWasteAccess &&
    wasteRestaurantId === restaurantId &&
    wasteList.some((w) => inRange(new Date(w.wasteDate), range));

  // Deps are store values + the time controls (never `range`/derived arrays, which are
  // rebuilt on every mount) so re-entering the report is a real cache hit, as for `data`.
  const enriched = hasPeriodWaste
    ? cachedReportValue(
        'utilization.enriched',
        [costPriceList, orders, customIngredients, showExVat, subrecipesById, revenue, byRestaurant, restaurantId, defaultWastePercentage],
        () =>
          enrichCostPriceListWithCurrentPrices(costPriceList, orders, {
            customIngredients,
            showExVat,
            allSubrecipes: subrecipesById,
            revenueHistory: revenue,
            byRestaurant,
            currentRestaurantId: restaurantId,
            defaultWastePercentage,
          })
      )
    : EMPTY_ENRICHED;

  const reportedWaste = hasPeriodWaste
    ? cachedReportValue(
        'utilization.reportedWaste',
        [wasteList, orders, enriched, subrecipesById, defaultWastePercentage, restaurantId, view, startDate, endDate],
        () =>
          computeWasteByProduct({
            wasteEntries: wasteList.filter((w) => inRange(new Date(w.wasteDate), range)),
            orders,
            enriched,
            subrecipesById,
            defaultWastePercentage,
            currentRestaurantId: restaurantId,
          })
      )
    : EMPTY_WASTE_BY_PRODUCT;

  const reportedWasteByKey = useMemo(
    () => new Map<string, WasteByProductRow>(reportedWaste.rows.map((r) => [r.key, r])),
    [reportedWaste]
  );

  /** The drawer holds a product KEY, not a row: the row is re-read from the (recomputed)
   *  report on every render, so a cost-price edit made from inside the drawer is reflected
   *  as soon as you come back to it. */
  const [drawerKey, setDrawerKey] = useState<string | null>(null);
  /** What `ProductSetupModal` needs to identify a product — reached from two places, so
   *  it holds the identity rather than a whole unmapped row. */
  const [setupTarget, setSetupTarget] = useState<{ platform: string; name: string; key: string } | null>(null);
  const [editCostPriceId, setEditCostPriceId] = useState<string | null>(null);

  const drawerRow = useMemo(
    () => (drawerKey ? data.rows.find((r) => r.key === drawerKey) ?? null : null),
    [drawerKey, data.rows]
  );

  const drawerPeriodLabel = useMemo(() => {
    const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
    return `${startDate.toLocaleDateString(undefined, opts)} – ${endDate.toLocaleDateString(undefined, opts)}`;
  }, [startDate, endDate]);

  // The drawer's "alternative products" section: the pool (or option set) the open row
  // belongs to. Pooled wins — it's the transitive set, so it's the fuller answer.
  const drawerGroupMembers = useMemo<UtilizationGroupMember[]>(() => {
    if (!drawerRow) return [];
    for (const groups of [data.pooled, data.grouped]) {
      const hit = groups.find((g) => g.members.length > 1 && g.members.some((m) => m.key === drawerRow.key));
      if (hit) return hit.members;
    }
    return [];
  }, [drawerRow, data.pooled, data.grouped]);

  // The page's single scope selection: one tab + one filter set, applied to the KPIs,
  // the chart, the Ordered-vs-Needed table and the donuts alike. Favorites is the
  // landing tab once anything is starred, else Over-ordered. Derived rather than seeded
  // into state because favourites arrive with the async restaurant load.
  const [pickedTab, setPickedTab] = useState<UtilizationTab | null>(null);
  const tab: UtilizationTab = pickedTab ?? (favoriteKeys.length > 0 ? 'favorites' : 'over');
  const [filters, setFilters] = useState<UtilizationFilters>(EMPTY_UTILIZATION_FILTERS);

  const sample = fixture
    ? data.rows.find(r => r.articleId === `${PRACTICE_ID_PREFIX}lamb`) ?? null
    : data.rows.find(r => isOverOrdered(r)) ?? data.rows[0] ?? null;
  useEffect(() => {
    if (!tourView) return;
    const stage = (event: Event) => {
      const name = (event as CustomEvent).detail?.stage;
      setFilters(EMPTY_UTILIZATION_FILTERS);
      if (name === 'setup' || name === 'setup-edit') {
        if (sample) setSetupTarget(old => old?.key === sample.key ? old : { platform: sample.platform, name: sample.name, key: sample.key });
        setEditCostPriceId(null);
        return;
      }
      setSetupTarget(null);
      setEditCostPriceId(name === 'edit' ? PRACTICE_DISH_ID : null);
      setDrawerKey(['drawer', 'edit'].includes(name) ? sample?.key ?? null : null);
      setPickedTab(name === 'unmapped' ? 'unmapped' : name === 'favorites' ? 'favorites' : name === 'favorite-pick' || (sample && !isOverOrdered(sample)) ? 'all' : 'over');
    };
    window.addEventListener('onboarding:utilization-stage', stage);
    return () => window.removeEventListener('onboarding:utilization-stage', stage);
  }, [tourView, sample]);

  // Active food/drinks sides from the shared type filter (unsupported values → both).
  const activeSides = useMemo<Array<'food' | 'drinks'>>(
    () => (overallTypeFilter === 'food' ? ['food'] : overallTypeFilter === 'drinks' ? ['drinks'] : ['food', 'drinks']),
    [overallTypeFilter]
  );

  if (
    !fixture && ((isLoadingOrders && orders.length === 0) ||
    (isLoadingRevenue && revenue.length === 0) ||
    (isLoadingCostPrice && costPriceList.length === 0))
  ) {
    return <ReportLoading />;
  }

  if (!data.hasMappableCostPrices) {
    return <EmptyCard message={t('reports.utilization.empty')} />;
  }

  // Scope pipeline: food/drinks side → shared filters → page tab. Everything below
  // reads `scopedRows`, so the KPIs, chart, table and donuts can never disagree.
  const sideRows = data.rows.filter((r) => activeSides.includes(r.side));
  const filteredRows = applyUtilizationFilters(sideRows, filters);
  const scopedRows = filterRowsByTab(filteredRows, tab, favoriteKeys);

  // The Not-linked tab is a different row type (spend only, no ordered/needed) — it
  // runs through the same shared filters, then swaps the view below the controls.
  const sideUnmapped = data.unmappedRows.filter((r) => activeSides.includes(r.side));
  const filteredUnmapped = applyUnmappedFilters(sideUnmapped, filters);
  const onUnmapped = isUnmappedTab(tab);

  // Tab counts sit on the filtered (pre-tab) rows, so each badge answers "how many
  // would I get if I clicked this" under the filters currently applied.
  const tabCounts: Record<UtilizationTab, number> = {
    favorites: filteredRows.filter((r) => favoriteKeys.includes(r.key)).length,
    over: filteredRows.filter(isOverOrdered).length,
    under: filteredRows.filter(isUnderOrdered).length,
    all: filteredRows.length,
    recipe: filteredRows.filter(isRecipeAttributable).length,
    unmapped: filteredUnmapped.length,
  };

  // Headline KPIs follow the tab + filters. The waste € and review count are plain
  // sums over the scoped rows (on the 'all' tab with no filters these reproduce the
  // parity-tested global numbers). The rate keeps the FULL period spend as its
  // denominator so it always reads as "share of my spend", comparable across tabs.
  const isAll = activeSides.length === 2;
  const scopedPotentialWaste = scopedRows.reduce((s, r) => s + (r.wasteEur !== null && r.wasteEur > 0 ? r.wasteEur : 0), 0);
  const sideSpend = activeSides.reduce((s, side) => s + data.spendBySide[side], 0);
  const overOrderingRate = sideSpend > 0 ? (scopedPotentialWaste / sideSpend) * 100 : 0;
  const productsToReview = scopedRows.filter((r) => getUtilizationStatus(r.utilizationPercent) !== 'healthy').length;
  const unmappedSpend = isAll ? data.unmappedFood.spend + data.unmappedDrinks.spend : sideUnmapped.reduce((s, r) => s + r.spend, 0);
  const unmappedCount = isAll ? data.unmappedFood.count + data.unmappedDrinks.count : sideUnmapped.length;

  // On the Not-linked tab the cards describe the filtered unmapped rows themselves.
  const filteredUnmappedSpend = filteredUnmapped.reduce((s, r) => s + r.spend, 0);

  if (tourView) publishUtilizationValues(scopedPotentialWaste, productsToReview, sample, practiceFavorites.length, data.rows.length);

  const scopedGroups = filterGroupedView(applyUtilizationFiltersToGroups(data.grouped, filters), activeSides, tab, favoriteKeys);
  const scopedPooled = filterGroupedView(applyUtilizationFiltersToGroups(data.pooled, filters), activeSides, tab, favoriteKeys);

  return (
    <PracticeScopeProvider value={tourView}>
    <div className="mt-4 sm:mt-6 flex flex-col pb-5">
      {tourView && <p role="status" className="mb-4 rounded-xl bg-amber-50 dark:bg-amber-950 p-3 text-sm text-amber-900 dark:text-amber-100">{t(fixture ? "onboarding.practiceNotice" : "onboarding.customerPracticeNotice")}</p>}
      <div data-onboarding="utilization-kpis">
        {onUnmapped ? (
          <UnmappedStatCards
            spend={filteredUnmappedSpend}
            count={filteredUnmapped.length}
            shareOfSpend={sideSpend > 0 ? (filteredUnmappedSpend / sideSpend) * 100 : 0}
          />
        ) : (
          <UtilizationStatCards
            potentialWasteEur={scopedPotentialWaste}
            overOrderingRate={overOrderingRate}
            productsToReview={productsToReview}
            unmappedSpend={unmappedSpend}
            unmappedCount={unmappedCount}
          />
        )}
      </div>

      <UtilizationControls
        rows={sideRows}
        unmappedRows={sideUnmapped}
        tab={tab}
        onTabChange={setPickedTab}
        counts={tabCounts}
        filters={filters}
        onFiltersChange={setFilters}
      />

      {/* Each table owns its own chart|table split so it can hand the chart the exact page
          of rows it is showing, plus their measured heights — that's what keeps bar N on
          the same baseline as row N. */}
      <div className="mb-6" data-onboarding={onUnmapped ? "utilization-table utilization-unmapped-table" : "utilization-table"}>
        {onUnmapped ? (
          <PurchasedNotLinkedTable
            key={`${restaurantId}:${view}:${startDate.getTime()}:${endDate.getTime()}`}
            restaurantId={restaurantId}
            rows={filteredUnmapped}
            totalSpend={filteredUnmappedSpend}
            canCheckAlternatives={canCheckAlternatives}
            defaultWastePercentage={defaultWastePercentage}
            onSetup={
              restaurantId && (!tourView || fixture) ? (r) => setSetupTarget({ platform: r.platform, name: r.name, key: r.key }) : undefined
            }
            chartTab={tab}
          />
        ) : (
          <OrderedVsNeededTable
            rows={scopedRows}
            fullRows={data.rows}
            restaurantId={restaurantId}
            canSuggestWasteActionItems={canSuggestWasteActionItems}
            wastePercentage={defaultWastePercentage}
            period={{ start: format(range.start, 'yyyy-MM-dd'), end: format(range.end, 'yyyy-MM-dd'), label: `${getTableFormattedDate(range.start)} – ${getTableFormattedDate(range.end)}` }}
            grouped={scopedGroups}
            pooled={scopedPooled}
            favoriteKeys={favoriteKeys}
            onToggleFavorite={handleToggleFavorite}
            onRowClick={(r) => setDrawerKey(r.key)}
            chartTab={tab}
          />
        )}
      </div>

      {!onUnmapped && <div><WasteBreakdownCharts rows={scopedRows} /></div>}

      <UsedDishesDrawer
        product={suspendOverlays ? null : drawerRow}
        onClose={() => setDrawerKey(null)}
        periodLabel={drawerPeriodLabel}
        groupMembers={drawerGroupMembers}
        onEditDish={(id) => { if (!tourView || (fixture && isPracticeId(id))) setEditCostPriceId(id); }}
        reportedWaste={hasWasteAccess && drawerRow ? reportedWasteByKey.get(drawerRow.key) ?? null : undefined}
        onAddLink={
          restaurantId && drawerRow && (!tourView || fixture)
            ? () => setSetupTarget({ platform: drawerRow.platform, name: drawerRow.name, key: drawerRow.key })
            : undefined
        }
        // Each of these renders its own dim + panel shadow on top of the drawer, so the
        // drawer drops its own (they doubled up) and stops answering Escape.
        overlayOpen={!!editCostPriceId || !!setupTarget}
      />

      {/* Cost-price editor for one dish of the drawer's breakdown. The drawer stays open
          behind it, so dismissing this lands back on the same product — with fresh numbers,
          since `drawerRow` is re-read from the recomputed report.
          The wrapper's z-index is load-bearing: the editor's own slide-over chrome is z-30,
          below this drawer's z-50, so it needs its own stacking context to sit on top. */}
      {!suspendOverlays && editCostPriceId && (
        <div className="relative z-[60]">
          <CostPriceDetailModal
            costPriceId={editCostPriceId}
            initialFocusProductKey={fixture ? sample?.key : undefined}
            onClose={() => setEditCostPriceId(null)}
            onBack={() => setEditCostPriceId(null)}
            defaultWastePercentage={defaultWastePercentage}
          />
        </div>
      )}

      {/* Utilization rows key their order product by the report productKey. Reached from
          the not-linked table's Setup action and from the drawer's "Add link". */}
      {!suspendOverlays && setupTarget && restaurantId && (
        <ProductSetupModal
          practice={tourView}
          identity={{
            platform: setupTarget.platform,
            productName: setupTarget.name,
            match: { kind: 'report', reportKey: setupTarget.key },
          }}
          restaurantId={restaurantId}
          onClose={() => setSetupTarget(null)}
        />
      )}
    </div>
    </PracticeScopeProvider>
  );
};

const UtilizationReport: React.FC = () => {
  const { active, session } = useUtilizationPractice();
  const restaurant = useAppUserDefaultStore(s => s.restaurant);
  if (isAllRestaurantsSelected(restaurant)) return <OrganizationUtilizationReport />;
  return <>
    <div hidden={active}><UtilizationReportBody /></div>
    {active && <UtilizationReportBody key={session} tourView />}
  </>;
};
export default UtilizationReport;
